import { Skeleton } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { DetailBar } from '@/widgets/DetailBar';
import styles from './TicketDetail.module.css';
import actionStyles from './TicketActions.module.css';

const LINE = { height: 12, radius: 6 } as const;

const SkelPerson: React.FC<{ name: number }> = ({ name }) => (
  <div className={styles.personRow}>
    <Skeleton circle width={28} height={28} />
    <span className={styles.skelPersonText}>
      <Skeleton width={name} {...LINE} />
      <Skeleton width={64} height={9} radius={6} />
    </span>
  </div>
);

const SkelHead: React.FC = () => (
  <div className={styles.head}>
    <Skeleton width={200} height={14} radius={6} />
    <Skeleton width="56%" height={24} radius="var(--radius-sm)" />
    <div className={styles.headMeta}>
      <Skeleton width={136} height={14} radius={6} />
      <Skeleton width={92} height={24} radius={999} />
      <Skeleton width={80} height={24} radius={999} />
    </div>
  </div>
);

const SkelFactsPerson: React.FC<{ name: number }> = ({ name }) => (
  <div className={styles.factsRequester}>
    <Skeleton circle width={40} height={40} />
    <span className={styles.skelPersonText}>
      <Skeleton width={name} {...LINE} />
      <Skeleton width={72} height={9} radius={6} />
    </span>
  </div>
);

export const TicketDetailSkeleton: React.FC<{
  backTo: string;
  backLabel?: string;
  leaving?: boolean;
}> = ({ backTo, backLabel, leaving }) => {
  const narrow = useMediaQuery('(max-width: 960px)');

  return (
    <div className={cn(styles.page, styles.skelEnter, leaving && styles.skelLeave)} aria-busy>
      <DetailBar
        backTo={backTo}
        backLabel={backLabel}
        noChatButton
        extra={
          <div className={actionStyles.bar}>
            <Skeleton width={126} height={32} radius={999} />
            { }
            <Skeleton width={102} height={32} radius={999} className={styles.skelWideOnly} />
            <Skeleton width={144} height={32} radius={999} className={styles.skelWideOnly} />
            <Skeleton circle width={32} height={32} />
          </div>
        }
      />

      {narrow && <SkelHead />}

      {narrow && (
        <div className={cn(styles.card, styles.factsCard)}>
          <SkelFactsPerson name={150} />
          <SkelFactsPerson name={124} />
          { }
          <Skeleton
            width="100%"
            height={52}
            radius="max(var(--radius-sm), calc(var(--radius-4xl) - var(--inset-m)))"
          />
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <div className={styles.mainViewport}>
            <div className={cn(styles.card, styles.summary)}>
              {!narrow && <SkelHead />}
              { }
              <Skeleton width={narrow ? '100%' : 300} height={44} radius={999} />
              <div className={styles.skelLines}>
                <Skeleton width="100%" {...LINE} />
                <Skeleton width="72%" {...LINE} />
              </div>
            </div>

            <div className={styles.chatCard}>
              <div className={styles.chatTabs}>
                <Skeleton width={40} height={18} radius={6} />
                <Skeleton width={72} height={18} radius={6} />
                <Skeleton width={72} height={18} radius={6} />
              </div>
              <div className={styles.skelChatBody}>
                <div className={styles.skelMsgIn}>
                  <Skeleton width="58%" height={56} radius="var(--radius-lg)" />
                </div>
                <div className={styles.skelMsgOut}>
                  <Skeleton width="46%" height={40} radius="var(--radius-lg)" />
                </div>
                <div className={styles.skelMsgIn}>
                  <Skeleton width="50%" height={40} radius="var(--radius-lg)" />
                </div>
              </div>
              <Skeleton width="100%" height={44} radius="var(--radius-lg)" />
            </div>
          </div>
        </div>

        { }
        <aside className={styles.sidebar}>
          <div className={styles.sidebarViewport}>
            {!narrow && (
              <>
                <div className={styles.card}>
                  <Skeleton
                    width="100%"
                    height={124}
                    radius="max(var(--radius-sm), calc(var(--radius-4xl) - var(--inset-l)))"
                  />
                </div>

                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    <Skeleton width={52} height={14} radius={6} />
                  </div>
                  <SkelPerson name={120} />
                  <SkelPerson name={140} />
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};
